/**
 * Карточка «Документы Bitrix24» — список файлов из всех активностей
 * привязанной к организации сделки. Подгружается лениво при раскрытии.
 *
 * Имени файла B24 через webhook не отдаёт, поэтому используем SUBJECT
 * активности (например «NDA c МТС согласовать») как название блока.
 * Ссылки ведут в B24 — пользователь там уже залогинен.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { api } from '../api/client';

interface BFile { id: number; url: string; }
interface BDoc {
  activity_id: string;
  subject: string;
  provider: string;
  created: string;
  files: BFile[];
}

const PROVIDER_LABEL: Record<string, string> = {
  CRM_TASKS_TASK: 'Задача',
  CRM_EMAIL: 'Письмо',
  IMOPENLINES_SESSION: 'Чат',
  REST_APP: 'Внешнее',
  '': '—',
};

export default function BitrixDocsCard({ orgId }: { orgId: string }) {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState<BDoc[] | null>(null);
  const [dealId, setDealId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // v0.9.51: статус сохранения файла в SD по file_id.
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [saved, setSaved] = useState<Record<number, string>>({});

  const saveToSd = async (file: BFile, subject: string) => {
    setSaving(s => ({ ...s, [file.id]: true }));
    try {
      const { data } = await api.post(`/api/v1/organizations/${orgId}/bitrix-documents/save`, {
        file_id: file.id, url: file.url, subject,
      });
      setSaved(s => ({ ...s, [file.id]: data.filename }));
    } catch (e: any) {
      alert('Не удалось сохранить в SD: ' + (e?.response?.data?.detail || e?.message || 'ошибка'));
    } finally {
      setSaving(s => { const n = { ...s }; delete n[file.id]; return n; });
    }
  };

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get(`/api/v1/organizations/${orgId}/bitrix-documents`);
      setDocs(data.documents || []);
      setDealId(data.deal_id ?? null);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && docs === null && !loading) load();
  }, [open]);

  const totalFiles = (docs || []).reduce((n, d) => n + d.files.length, 0);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40">
        <span className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
          <FileText size={14} className="text-blue-400" />
          <span className="font-bold text-white text-sm">Документы Bitrix24</span>
          {docs && totalFiles > 0 && (
            <span className="text-[10px] text-slate-500 font-mono">({totalFiles} файлов)</span>
          )}
        </span>
        {dealId && (
          <a href={`https://bx24.valocloud.ru/crm/deal/details/${dealId}/`}
             target="_blank" rel="noopener noreferrer"
             onClick={e => e.stopPropagation()}
             className="text-[10px] text-slate-500 hover:text-blue-400 inline-flex items-center gap-1">
            Сделка #{dealId} <ExternalLink size={9} />
          </a>
        )}
      </button>

      {open && (
        <div className="px-4 py-3 border-t border-slate-800">
          {loading ? (
            <div className="flex justify-center py-3"><Loader2 size={14} className="animate-spin text-blue-400" /></div>
          ) : err ? (
            <div className="text-xs text-red-400">{err}</div>
          ) : !docs || docs.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-3">
              {dealId === null ? 'У организации нет привязки к сделке Bitrix24.' : 'В сделке нет активностей с файлами.'}
            </div>
          ) : (
            <ul className="space-y-2">
              {docs.map(d => (
                <li key={d.activity_id} className="bg-slate-950/40 border border-slate-800 rounded p-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-xs text-white min-w-0 truncate" title={d.subject}>
                      {d.subject || '(без темы)'}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono shrink-0">
                      {PROVIDER_LABEL[d.provider] ?? d.provider}
                      {d.created && ` · ${new Date(d.created).toLocaleDateString('ru-RU')}`}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {d.files.map(f => (
                      <span key={f.id} className="inline-flex items-center gap-1">
                        <a href={f.url} target="_blank" rel="noopener noreferrer"
                           title="Открыть в Bitrix24 (скачается на ваш компьютер)"
                           className="inline-flex items-center gap-1 text-[11px] bg-slate-800 hover:bg-slate-700 text-blue-300 hover:text-blue-200 px-2 py-0.5 rounded">
                          <FileText size={10} /> #{f.id}
                        </a>
                        {saved[f.id] ? (
                          <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1"
                                title={`Сохранено в SD как «${saved[f.id]}»`}>
                            ✓ в SD
                          </span>
                        ) : (
                          <button onClick={() => saveToSd(f, d.subject)}
                                  disabled={!!saving[f.id]}
                                  title="Скачать файл из Bitrix24 и сохранить в карточке клиента SD"
                                  className="text-[10px] bg-slate-800 hover:bg-emerald-900/50 text-slate-400 hover:text-emerald-300 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                            {saving[f.id] ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                            в SD
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
